import React, { useEffect } from "react";

import { useAppRouteParams } from "@/AppRoutes";
import {
  postAdminDisableFrontendAddon,
  postAdminEnableFrontendAddon,
} from "@/api/admin/frontendAddons";
import {
  postDisableFrontendAddon,
  postEnableFrontendAddon,
} from "@/api/frontendAddons";
import { useAppAccount } from "@/components/AppProvider/AppAccountProvider";
import {
  BulkActionRequest,
  createIdleRequest,
  useBulkActionToast,
} from "@/components/AppProvider/AppNotificationProvider/useBulkActionToast";
import { usePermissions } from "@/components/InOrganizationProvider/PermissionsProvider";

export type FrontendAddonEnableRequest = BulkActionRequest & {
  isSelectionBarAction?: boolean;
};

/**
 * 選択されたフロントエンドアドオンを即時有効化し、Toast で結果を通知するフック。
 * BulkActionRequest.phase が "requested" のときに実行する。
 */
export const useFrontendAddonEnableAction = ({
  enableRequest,
  setEnableRequest,
  setSelectedFrontendAddonIds,
  runReloadFrontendAddons,
}: {
  enableRequest: FrontendAddonEnableRequest;
  setEnableRequest: React.Dispatch<
    React.SetStateAction<FrontendAddonEnableRequest>
  >;
  setSelectedFrontendAddonIds: React.Dispatch<React.SetStateAction<string[]>>;
  runReloadFrontendAddons: () => Promise<void>;
}) => {
  const { organizationId } = useAppRouteParams();
  const { isAADeveloperOrAbove } = useAppAccount();
  const { hasPermission } = usePermissions();
  const hasWritePermission =
    isAADeveloperOrAbove || hasPermission("frontend_addons_owned", "write");

  const { runWithToast } = useBulkActionToast({
    success: (count) =>
      count === 1
        ? "フロントエンドアドオンを有効化しました"
        : `${count}件のフロントエンドアドオンを有効化しました`,
    error: (count) =>
      count === 1
        ? "フロントエンドアドオンを有効化できませんでした"
        : `${count}件のフロントエンドアドオンを有効化できませんでした`,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (enableRequest.phase !== "requested") return;

    const itemIds = enableRequest.itemIds;
    if (itemIds.length === 0) {
      setEnableRequest(createIdleRequest());
      return;
    }

    // 即時 in_progress へ遷移してリマウント時の二重実行を防ぐ
    setEnableRequest({ ...enableRequest, phase: "in_progress" });

    const runAll = async () => {
      const { failedIds } = await runWithToast(
        itemIds.map((id) => ({ id })),
        async ({ id }) => {
          isAADeveloperOrAbove
            ? await postAdminEnableFrontendAddon({ frontendAddonId: id })
            : await postEnableFrontendAddon({
                organizationId,
                frontendAddonId: id,
              });
        },
        {
          undoItem: hasWritePermission
            ? async (_result, item) => {
                isAADeveloperOrAbove
                  ? await postAdminDisableFrontendAddon({
                      frontendAddonId: item.id,
                    })
                  : await postDisableFrontendAddon({
                      organizationId,
                      frontendAddonId: item.id,
                    });
                await runReloadFrontendAddons();
              }
            : undefined,
        }
      );

      const successfulIds = itemIds.filter((id) => !failedIds.includes(id));
      setSelectedFrontendAddonIds((prev) =>
        enableRequest.isSelectionBarAction
          ? failedIds
          : prev.filter((id) => !successfulIds.includes(id))
      );

      try {
        await runReloadFrontendAddons();
      } catch {
        // 有効化の成功/失敗は通知済みのため、再読込の失敗で矛盾した通知を出さない
      }

      setEnableRequest(createIdleRequest());
    };

    runAll();
  }, [enableRequest.phase]);
};
